// ./src/components/generic/MediaImageField/MediaImageField.tsx
//
// A reusable, CONTROLLED single-image field (a logo / avatar / one media slot).
// One MediaSelector pick becomes a { uploadedFileId?, url } ref that persists on
// save and survives a reload (the caller seeds `url` from the resolved file URL
// when loading a record). The parent owns the value, so this works before the
// record exists too. For a multi-image gallery use MediaGalleryField instead.
//
// On submit the caller sends uploadedFileId for library/upload picks (letting the
// server resolve the URL) and url for stock/URL picks — mirroring the gallery.

import React, { useState } from "react";
import { Stack, Group, Text, Box, ActionIcon, Button } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic/AppIcon";
import { MediaSelector, MediaSelectorResult, MediaIntent } from "@/components/generic/MediaSelector";
import { MediaImageRef } from "@/components/generic/MediaGalleryField";

export interface MediaImageFieldProps {
  /** What the image is for — drives the storage purpose, crop aspect and tabs. */
  intent: MediaIntent;
  value: MediaImageRef | null;
  onChange: (value: MediaImageRef | null) => void;
  /** Optional context path to organize fresh uploads in the account library. */
  contextPath?: string | null;
  label?: string;
  description?: string;
  addLabel?: string;
  emptyText?: string;
  /** Preview aspect ratio (width / height). Defaults to 1 (square) — good for logos. */
  aspect?: number;
  /** Override the intent's default for allowing a pasted URL result. */
  allowUrl?: boolean;
  disabled?: boolean;
  /**
   * Keep REMOVE working while `disabled` — for a control the plan no longer allows (A2e #88): the host may not add or
   * replace the image, but must always be able to take it off, which the server always accepts.
   */
  removableWhenDisabled?: boolean;
  /** Replaces the default thumbnail when there is an image — e.g. a preview showing how it will be painted. */
  preview?: React.ReactNode;
  /**
   * Server refusal about the image field. Without it a drawer that lists the key
   * in KNOWN_FORM_FIELDS swallows the message entirely — see the em-scaffold-ui
   * `KNOWN_FORM_FIELDS` rule.
   */
  error?: string | null;
}

export const MediaImageField: React.FC<MediaImageFieldProps> = ({
  intent,
  value,
  onChange,
  contextPath,
  label,
  description,
  addLabel,
  emptyText,
  aspect = 1,
  allowUrl,
  disabled = false,
  removableWhenDisabled = false,
  preview,
  error,
}) => {
  const { t } = useTranslation(["common"]);

  const [pickerOpen, setPickerOpen] = useState(false);

  const handleSelect = (result: MediaSelectorResult) => {
    if (!result.uploadedFileId && !result.url) {
      onChange(null);

      return;
    }

    onChange({ uploadedFileId: result.uploadedFileId ?? null, url: result.url ?? null });
  };

  const hasImage = Boolean(value?.url);

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text size="md" fw={500}>
            {label ?? t("common:mediaImage.label", { defaultValue: "Image" })}
          </Text>
          {description ? (
            <Text size="sm" c="dimmed">
              {description}
            </Text>
          ) : null}
        </Stack>

        <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
          <Button
            size="md"
            variant="light"
            leftSection={<AppIcon iconName="Photo" size={16} />}
            onClick={() => setPickerOpen(true)}
            disabled={disabled}
          >
            {hasImage
              ? t("common:buttons.replace", { defaultValue: "Replace" })
              : addLabel ?? t("common:mediaImage.add", { defaultValue: "Add image" })}
          </Button>

          {hasImage ? (
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => onChange(null)}
              aria-label={t("common:buttons.remove", { defaultValue: "Remove" })}
              disabled={disabled && !removableWhenDisabled}
            >
              <AppIcon iconName="Trash" size={16} />
            </ActionIcon>
          ) : null}
        </Group>
      </Group>

      {hasImage && preview ? (
        preview
      ) : hasImage ? (
        <Box
          style={{
            width: "100%",
            maxWidth: 220,
            aspectRatio: String(aspect),
            borderRadius: "var(--mantine-radius-sm)",
            border: "1px solid var(--mantine-color-default-border)",
            overflow: "hidden",
            background: "var(--mantine-color-gray-0)",
          }}
        >
          <img
            src={value?.url ?? undefined}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          />
        </Box>
      ) : (
        <Text size="sm" c="dimmed">
          {emptyText ??
            t("common:mediaImage.empty", {
              defaultValue: "No image yet. Upload or pick one from your library.",
            })}
        </Text>
      )}

      {error ? (
        <Text size="sm" c="red">
          {error}
        </Text>
      ) : null}

      <MediaSelector
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        intent={intent}
        value={value ? { uploadedFileId: value.uploadedFileId, url: value.url } : null}
        onSelect={handleSelect}
        contextPath={contextPath ?? null}
        {...(allowUrl !== undefined ? { allowUrl } : {})}
      />
    </Stack>
  );
};
